import { useRef, useState } from 'react';
import ModalWrapper from '../../components/modals/ModalWrapper';
import ModalHeader from '../../components/modals/ModalHeader';
import { IonButton, IonContent, IonLoading } from '@ionic/react';
import SignupForm, { SignupFormHandle } from '../sign-up/SignupForm';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import { SignupSchema } from '../sign-up/schema';
import { useUpdateProfileMutation } from '../../../../store/api-slices';
import { IModal } from '../../router/ModalRouter';
import { useTranslation } from 'react-i18next';
import Alert, { AlertButtonRole } from '../../components/modals/Alert';
import useAuth from '../../../../hooks/global/useAuth';
import AuthorizationManager from '../../../../services/auth/AuthorizationManager';
import toast from 'react-hot-toast';

const DeleteConfirmationModal = ({
  isOpen,
  onDismiss,
  onSuccess,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
}) => {
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Features:Profile:Components:ProfileModal:NestedModals:DeleteConfirmationModal',
  });

  const { logout } = useAuth();

  const [loading, setLoading] = useState(false);

  const onDelete = async () => {
    setLoading(true);
    const { success } = await AuthorizationManager.deleteAccount();
    setLoading(false);

    if (!success) {
      return toast.error(
        t('toasts.error', { supportEmail: company?.service_provider?.support_email ?? 'support@foodamigos.io' }),
      );
    }

    toast.success(t('toasts.success'));
    logout(false);
    onSuccess();
  };

  return (
    <>
      <IonLoading isOpen={loading} message={t('labels.loading')} spinner="crescent" />
      <Alert
        title={t('title')}
        description={t('description')}
        isOpen={isOpen}
        onDidDismiss={onDismiss}
        buttons={[
          {
            text: t('buttons.confirm'),
            role: AlertButtonRole.Destructive,
            handler: onDelete,
          },
          {
            text: t('buttons.cancel'),
            role: AlertButtonRole.Cancel,
          },
        ]}
      />
    </>
  );
};

const ManageAccountModal = ({ goBack, closeModal }: Partial<IModal>) => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Features:Profile:Components:ProfileModal:NestedModals:ManageAccountModal',
  });

  const formRef = useRef<SignupFormHandle>(null);
  const profile = useSelector((state: StoreInterface) => state.profile.profile);
  const [updateProfile] = useUpdateProfileMutation();

  const [loading, setLoading] = useState<boolean>(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const toggleDeleteConfirmation = (): void => {
    setIsDeleteConfirmationOpen((prev) => !prev);
  };

  const onSubmit = (data: SignupSchema): void => {
    const { company_id, phone_number: _, ...rest } = data;
    setLoading(true);
    updateProfile({
      companyId: company_id,
      data: rest,
    })
      .then(() => closeModal())
      .finally(() => setLoading(false));
  };

  return (
    <>
      <ModalWrapper className="mt-4">
        <ModalHeader
          title={t('title')}
          onBack={() => goBack()}
          endText={t('buttons.save')}
          loading={loading}
          onEndClick={() => formRef.current.submit()}
        />
        <IonContent className="min-h-[75dvh] [&::part(scroll)]:py-4 space-y-md">
          <SignupForm ref={formRef} defaultValues={profile} onSubmit={onSubmit} />
          <IonButton
            className="mx-auto px-md"
            expand="full"
            fill="clear"
            color="danger"
            onClick={() => toggleDeleteConfirmation()}
          >
            {t('buttons.delete')}
          </IonButton>
          <DeleteConfirmationModal
            isOpen={isDeleteConfirmationOpen}
            onDismiss={toggleDeleteConfirmation}
            onSuccess={closeModal}
          />
        </IonContent>
      </ModalWrapper>
    </>
  );
};

export default ManageAccountModal;
